import { useState, useEffect } from 'react';
import { landingAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const inp  = { width:'100%', padding:'9px 12px', border:'1.5px solid #E5E7EB', borderRadius:8, outline:'none', boxSizing:'border-box', fontSize:'.9rem' };
const lbl  = { display:'block', fontWeight:600, fontSize:'.8rem', marginBottom:5, color:'#374151' };
const card = { background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24, marginBottom:16 };

export default function LandingEditor() {
  const { user } = useAuth();
  const [landing, setLanding]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [slides, setSlides]           = useState([]);
  const [gallery, setGallery]         = useState([]);
  const [reviews, setReviews]         = useState([]);
  const [reviewForm, setReviewForm]   = useState({ author_name:'', rating:5, comment:'', is_active:1 });
  const [editingReview, setEditingReview] = useState(null);
  const [slideFile, setSlideFile]     = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [form, setForm]           = useState({
    title:'', subtitle:'', about:'', cta_text:'Agendar agora', cta_whatsapp:'',
    phone:'', email:'', address:'', city:'', state:'', zip_code:'',
    working_hours:'', facebook_url:'', instagram_url:'',
    color_primary:'#2563EB', color_secondary:'#0F172A',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, rv] = await Promise.all([landingAPI.show(), landingAPI.reviews()]);
      const d = r.data.data;
      setLanding(d);
      setReviews(rv.data.data || []);
      if (d) {
        setForm({
          title:           d.title            || '',
          subtitle:        d.subtitle         || '',
          about:           d.about            || '',
          cta_text:        d.cta_text         || 'Agendar agora',
          cta_whatsapp:    d.cta_whatsapp     || '',
          phone:           d.phone            || '',
          email:           d.email            || '',
          address:         d.address          || '',
          city:            d.city             || '',
          state:           d.state            || '',
          zip_code:        d.zip_code         || '',
          working_hours:   d.working_hours    || '',
          facebook_url:    d.facebook_url     || '',
          instagram_url:   d.instagram_url    || '',
          color_primary:   d.color_primary    || '#2563EB',
          color_secondary: d.color_secondary  || '#0F172A',
        });
        setSlides(d.slides || []);
        setGallery(d.gallery || []);
      }
    } catch { toast.error('Erro ao carregar configurações.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await landingAPI.update(form);
      toast.success('Configurações salvas!');
    } catch (err) { toast.error(err.response?.data?.message || 'Erro.'); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    try { await landingAPI.publish(); toast.success('Página publicada!'); load(); }
    catch { toast.error('Erro ao publicar.'); }
  };

  const addSlide = async () => {
    if (!slideFile) return;
    const fd = new FormData(); fd.append('image', slideFile);
    try { await landingAPI.addSlide(fd); toast.success('Slide adicionado!'); setSlideFile(null); load(); }
    catch { toast.error('Erro ao adicionar slide.'); }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm('Remover slide?')) return;
    try { await landingAPI.deleteSlide(id); toast.success('Slide removido.'); load(); }
    catch { toast.error('Erro.'); }
  };

  const addGallery = async () => {
    if (!galleryFile) return;
    const fd = new FormData(); fd.append('image', galleryFile);
    try { await landingAPI.uploadGallery(fd); toast.success('Foto adicionada!'); setGalleryFile(null); load(); }
    catch { toast.error('Erro ao adicionar foto.'); }
  };

  const deleteGallery = async (id) => {
    if (!window.confirm('Remover foto?')) return;
    try { await landingAPI.deleteGallery(id); toast.success('Foto removida.'); load(); }
    catch { toast.error('Erro.'); }
  };

  const saveReview = async (e) => {
    e.preventDefault();
    try {
      if (editingReview) {
        await landingAPI.updateReview(editingReview.id, reviewForm);
        toast.success('Avaliação atualizada!');
      } else {
        await landingAPI.createReview(reviewForm);
        toast.success('Avaliação adicionada!');
      }
      setReviewForm({ author_name:'', rating:5, comment:'', is_active:1 });
      setEditingReview(null);
      const rv = await landingAPI.reviews();
      setReviews(rv.data.data || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Erro.'); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Remover avaliação?')) return;
    try {
      await landingAPI.deleteReview(id);
      toast.success('Avaliação removida.');
      const rv = await landingAPI.reviews();
      setReviews(rv.data.data || []);
    } catch { toast.error('Erro.'); }
  };

  const startEditReview = (r) => {
    setEditingReview(r);
    setReviewForm({ author_name: r.author_name, rating: r.rating, comment: r.comment || '', is_active: r.is_active });
  };

  const slug = user?.tenant_slug || '';
  const pageUrl = `/estetica/${slug}`;

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#6B7280' }}>Carregando...</div>;

  const TABS = [['info','Informações'],['appearance','Aparência'],['slides','Carrossel'],['gallery','Galeria'],['reviews','Avaliações']];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'#0F172A', margin:0 }}>Landing Page</h1>
          {slug && (
            <a href={pageUrl} target="_blank" rel="noreferrer"
              style={{ fontSize:'.8rem', color:'#2563EB', textDecoration:'none' }}>
              🔗 /estetica/{slug} ↗
            </a>
          )}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={save} disabled={saving} style={{ padding:'10px 20px', border:'1.5px solid #2563EB', borderRadius:8, background:'#EFF6FF', color:'#2563EB', fontWeight:700, cursor:'pointer' }}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
          <button onClick={publish} style={{ padding:'10px 20px', background:'#059669', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>
            {(landing?.status === 'published' || landing?.is_published) ? '✓ Publicado' : 'Publicar'}
          </button>
        </div>
      </div>

      {landing && !landing.is_published && landing.status !== 'published' && (
        <div style={{ background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:'.85rem', color:'#92400E', fontWeight:600 }}>
          ⚠️ Sua landing page ainda não está publicada. Clique em "Publicar" para que seus clientes possam visualizá-la.
        </div>
      )}

      <div style={{ display:'flex', gap:0, borderBottom:'2px solid #E5E7EB', marginBottom:20 }}>
        {TABS.map(([t,l]) => (
          <button key={t} onClick={()=>setActiveTab(t)}
            style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontWeight:700, fontSize:'.9rem',
              color: activeTab===t?'#2563EB':'#6B7280', borderBottom: activeTab===t?'2px solid #2563EB':'2px solid transparent', marginBottom:-2 }}>
            {l}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <div>
          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Textos principais</h3>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Título principal</label>
              <input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Ex: Sua estética com qualidade top" style={inp} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Subtítulo</label>
              <input value={form.subtitle} onChange={e=>set('subtitle',e.target.value)} placeholder="Ex: Cuidamos do seu veículo com dedicação" style={inp} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Sobre a empresa</label>
              <textarea value={form.about} onChange={e=>set('about',e.target.value)} rows={4} style={{ ...inp, resize:'vertical' }} placeholder="Conte a história da sua empresa..." />
            </div>
          </div>

          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Botão CTA e WhatsApp</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={lbl}>Texto do botão CTA</label>
                <input value={form.cta_text} onChange={e=>set('cta_text',e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>WhatsApp (com DDD)</label>
                <input value={form.cta_whatsapp} onChange={e=>set('cta_whatsapp',e.target.value)} placeholder="5511999999999" style={inp} />
              </div>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Contato e endereço</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {[['phone','Telefone'],['email','E-mail'],['address','Endereço'],['city','Cidade'],['state','Estado'],['zip_code','CEP']].map(([k,l]) => (
                <div key={k}>
                  <label style={lbl}>{l}</label>
                  <input value={form[k]} onChange={e=>set(k,e.target.value)} style={inp} />
                </div>
              ))}
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Horário de funcionamento</label>
                <input value={form.working_hours} onChange={e=>set('working_hours',e.target.value)} placeholder="Ex: Seg-Sex: 8h às 18h | Sáb: 8h às 13h" style={inp} />
              </div>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Redes sociais</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {[['instagram_url','Instagram'],['facebook_url','Facebook']].map(([k,l]) => (
                <div key={k}>
                  <label style={lbl}>{l} (URL)</label>
                  <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder="https://..." style={inp} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'appearance' && (
        <div style={card}>
          <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Cores da página</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div>
              <label style={lbl}>Cor primária (botões, destaques)</label>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input type="color" value={form.color_primary} onChange={e=>set('color_primary',e.target.value)}
                  style={{ width:48, height:40, borderRadius:8, border:'none', cursor:'pointer', padding:2 }} />
                <input value={form.color_primary} onChange={e=>set('color_primary',e.target.value)} style={{ ...inp, flex:1 }} />
              </div>
            </div>
            <div>
              <label style={lbl}>Cor secundária (cabeçalho, fundo escuro)</label>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input type="color" value={form.color_secondary} onChange={e=>set('color_secondary',e.target.value)}
                  style={{ width:48, height:40, borderRadius:8, border:'none', cursor:'pointer', padding:2 }} />
                <input value={form.color_secondary} onChange={e=>set('color_secondary',e.target.value)} style={{ ...inp, flex:1 }} />
              </div>
            </div>
          </div>
          <div style={{ marginTop:24, borderRadius:12, overflow:'hidden', border:'1px solid #E5E7EB' }}>
            <div style={{ background:form.color_secondary||'#0F172A', padding:'20px 24px' }}>
              <span style={{ color:'#fff', fontWeight:800, fontSize:'1.2rem' }}>🚗 Prévia</span>
            </div>
            <div style={{ padding:24, background:'#F8FAFC' }}>
              <h2 style={{ color:form.color_secondary||'#0F172A', fontWeight:900 }}>{form.title||'Título da sua empresa'}</h2>
              <p style={{ color:'#6B7280' }}>{form.subtitle||'Subtítulo aqui'}</p>
              <button style={{ background:form.color_primary||'#2563EB', color:'#fff', border:'none', borderRadius:8, padding:'10px 24px', fontWeight:700, cursor:'pointer', marginTop:8 }}>
                {form.cta_text||'Agendar agora'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'slides' && (
        <div>
          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Adicionar slide</h3>
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              <input type="file" accept="image/*" onChange={e=>setSlideFile(e.target.files[0])} style={{ flex:1 }} />
              <button onClick={addSlide} disabled={!slideFile} style={{ padding:'9px 20px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:slideFile?'pointer':'not-allowed', opacity:slideFile?1:0.6 }}>
                Adicionar
              </button>
            </div>
            <p style={{ fontSize:'.75rem', color:'#9CA3AF', marginTop:8 }}>Recomendado: 1200×500px, JPEG ou PNG, máx. 5MB</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
            {slides.length === 0 && <p style={{ color:'#9CA3AF', padding:20, gridColumn:'1/-1', textAlign:'center' }}>Nenhum slide. Adicione imagens acima.</p>}
            {slides.map((s, i) => (
              <div key={s.id} style={{ border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden', position:'relative' }}>
                <img src={s.image_url} alt={`Slide ${i+1}`} style={{ width:'100%', aspectRatio:'2.4', objectFit:'cover' }} />
                <div style={{ padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:'.8rem', color:'#6B7280' }}>Slide {i+1}</span>
                  <button onClick={()=>deleteSlide(s.id)} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:'.8rem', fontWeight:600 }}>Remover</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'gallery' && (
        <div>
          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:16, color:'#0F172A' }}>Adicionar foto à galeria</h3>
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              <input type="file" accept="image/*" onChange={e=>setGalleryFile(e.target.files[0])} style={{ flex:1 }} />
              <button onClick={addGallery} disabled={!galleryFile} style={{ padding:'9px 20px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:galleryFile?'pointer':'not-allowed', opacity:galleryFile?1:0.6 }}>
                Adicionar
              </button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
            {gallery.length === 0 && <p style={{ color:'#9CA3AF', padding:20, gridColumn:'1/-1', textAlign:'center' }}>Nenhuma foto na galeria.</p>}
            {gallery.map(g => (
              <div key={g.id} style={{ border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
                <img src={g.image_url} alt="Galeria" style={{ width:'100%', aspectRatio:'1', objectFit:'cover' }} />
                <div style={{ padding:'8px 12px', textAlign:'right' }}>
                  <button onClick={()=>deleteGallery(g.id)} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:6, padding:'3px 10px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>Remover</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'reviews' && (
        <div>
          <div style={card}>
            <h3 style={{ fontWeight:700, marginBottom:4, color:'#0F172A' }}>{editingReview ? 'Editar avaliação' : 'Nova avaliação'}</h3>
            <p style={{ color:'#6B7280', fontSize:'.85rem', marginBottom:16 }}>
              Adicione depoimentos de clientes satisfeitos para exibir na landing page.
            </p>
            <form onSubmit={saveReview}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:14, marginBottom:14 }}>
                <div>
                  <label style={lbl}>Nome do cliente *</label>
                  <input value={reviewForm.author_name} onChange={e=>setReviewForm(f=>({...f,author_name:e.target.value}))} required style={inp} placeholder="Ex: João Silva" />
                </div>
                <div>
                  <label style={lbl}>Nota *</label>
                  <select value={reviewForm.rating} onChange={e=>setReviewForm(f=>({...f,rating:parseInt(e.target.value)}))} style={{ ...inp, width:'auto' }}>
                    {[5,4,3,2,1].map(n=><option key={n} value={n}>{'★'.repeat(n)} ({n})</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Depoimento</label>
                <textarea value={reviewForm.comment} onChange={e=>setReviewForm(f=>({...f,comment:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} placeholder="O que o cliente disse sobre o serviço..." />
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:'.85rem', color:'#374151' }}>
                  <input type="checkbox" checked={!!reviewForm.is_active} onChange={e=>setReviewForm(f=>({...f,is_active:e.target.checked?1:0}))} />
                  Exibir na landing page
                </label>
                <div style={{ flex:1 }} />
                {editingReview && (
                  <button type="button" onClick={()=>{setEditingReview(null);setReviewForm({author_name:'',rating:5,comment:'',is_active:1})}}
                    style={{ padding:'8px 16px', border:'1.5px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer' }}>Cancelar</button>
                )}
                <button type="submit" style={{ padding:'9px 24px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>
                  {editingReview ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {reviews.length === 0 && <p style={{ color:'#9CA3AF', textAlign:'center', padding:'32px 0' }}>Nenhuma avaliação cadastrada ainda.</p>}
            {reviews.map(r => (
              <div key={r.id} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'16px 20px', display:'flex', gap:16, alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <span style={{ fontWeight:700, color:'#0F172A' }}>{r.author_name}</span>
                    <span style={{ color:'#F59E0B', fontWeight:700 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</span>
                    {!r.is_active && <span style={{ background:'#F3F4F6', color:'#6B7280', borderRadius:6, padding:'1px 8px', fontSize:'.7rem', fontWeight:600 }}>Oculto</span>}
                  </div>
                  {r.comment && <p style={{ color:'#6B7280', fontSize:'.85rem', margin:0, fontStyle:'italic' }}>"{r.comment}"</p>}
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={()=>startEditReview(r)} style={{ background:'#EFF6FF', color:'#2563EB', border:'none', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>Editar</button>
                  <button onClick={()=>deleteReview(r.id)} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>Remover</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
